export interface WaveformData {
    values: Float32Array;
    maxValue: number;
}

export interface WaveformPath {
    path: Path2D;
    maxValue: number;
}

export const createWaveformData = (
    channels: readonly Float32Array[],
    widths: readonly number[],
): WaveformData[] => {
    const numChannels = channels.length;
    const numFrames = channels[0]?.length;

    if (numChannels === 0 || numFrames === 0) {
        return widths.map(
            (w): WaveformData => ({ maxValue: 0.0, values: new Float32Array(Math.floor(w)) }),
        );
    }

    const getValue = (i: number) => {
        let value = 0.0;
        for (const channel of channels) {
            const abs = Math.abs(channel[i]);
            const clamped = Math.min(abs, 1.0); // apparently some samples clip!
            value = Math.max(value, clamped);
        }
        return value;
    };

    const results: WaveformData[] = [];
    for (const w of widths) {
        const width = Math.floor(w);
        if (width <= 0) {
            continue;
        }

        const result: WaveformData = { values: new Float32Array(width), maxValue: 0.0 };
        results.push(result);

        if (numFrames < width) {
            // Upsample: iterate over pixels, map each to a frame
            const framesPerPixel = numFrames / width;
            for (let x = 0; x < width; x++) {
                const value = getValue(Math.floor(x * framesPerPixel));
                result.values[x] = value;
                result.maxValue = Math.max(result.maxValue, value);
            }
        } else {
            // Downsample: iterate over frames, accumulate into windows
            const windowLength_float = numFrames / width;
            let windowX = 0;
            let windowValue_0to1 = 0.0;
            let windowEndIndex = Math.floor(windowLength_float);
            for (let i = 0; i < numFrames; i++) {
                const value = getValue(i);
                windowValue_0to1 = Math.max(windowValue_0to1, value);
                const isLastFrameOfWindow = i + 1 === windowEndIndex;
                if (isLastFrameOfWindow) {
                    result.values[windowX] = windowValue_0to1;
                    result.maxValue = Math.max(result.maxValue, windowValue_0to1);
                    windowX += 1;
                    windowValue_0to1 = 0.0;
                    windowEndIndex = Math.floor((windowX + 1) * windowLength_float);
                }
            }
        }
    }

    return results;
};

export const createWaveformPoints_0to1 = (
    data: Readonly<Float32Array>,
    addPoint: (x: number, y: number) => void,
): void => {
    const numValues = data.length;
    const xScale = numValues > 1 ? 1 / (numValues - 1) : 1;
    for (let i = 0; i < numValues; i++) {
        addPoint(i * xScale, -(data[i] * 0.5));
    }
    for (let i = numValues - 1; i >= 0; i--) {
        addPoint(i * xScale, data[i] * 0.5);
    }
};

export const createWaveformPath_0to1 = (data: Readonly<Float32Array>): Path2D => {
    const path = new Path2D();
    createWaveformPoints_0to1(data, path.lineTo.bind(path));
    path.closePath();
    return path;
};

export const scaleWaveform0to1 = (
    waveformPath: WaveformPath,
    width: number,
    height: number,
    shouldNormalizeHeight: boolean,
) => {
    const scaledPath = new Path2D();
    const scaleHeight = shouldNormalizeHeight ? (1.0 / waveformPath.maxValue) * height : height;
    const matrix = new DOMMatrix().translateSelf(0, height / 2).scaleSelf(width, scaleHeight);
    scaledPath.addPath(waveformPath.path, matrix);
    return scaledPath;
};

interface WaveformLevel {
    readonly width: number;
    readonly maxValue: number;
    readonly normalizedPath: WaveformPath;
    cache: {
        width: number;
        height: number;
        shouldNormalizeHeight: boolean;
        path: Path2D;
    };
}

export interface ScalableWaveform {
    /**
     * Get a Path2D for the waveform, scaled to the requested width and height.
     * Caches a single value — repeated calls with the same arguments return the cached path.
     * @param width - Target width in pixels
     * @param height - Target height in pixels
     * @param shouldNormalizeHeight - When true, scales the waveform so the loudest peak fills the full height
     */
    getPath: (width: number, height: number, shouldNormalizeHeight: boolean) => Path2D;
}

export const createScalableWaveform = (
    audioChannels: readonly Float32Array[],
    maxWidth_px: number,
): ScalableWaveform => {
    // Generate levels at max, 1/2, 1/4, 1/8 in a single pass
    const widths = [maxWidth_px, maxWidth_px / 2, maxWidth_px / 4, maxWidth_px / 8];
    const waveformData = createWaveformData(audioChannels, widths);

    const levels: WaveformLevel[] = [];
    for (const data of waveformData) {
        if (data.values.length === 0) {
            continue;
        }
        levels.push({
            width: data.values.length,
            maxValue: data.maxValue,
            normalizedPath: {
                path: createWaveformPath_0to1(data.values),
                maxValue: data.maxValue,
            },
            cache: {
                width: 0,
                height: 0,
                shouldNormalizeHeight: false,
                path: new Path2D(),
            },
        });
    }

    return {
        getPath: (width: number, height: number, shouldNormalizeHeight: boolean) => {
            // Use the smallest level that is >= requested width
            let level = levels[0];
            for (let i = levels.length - 1; i >= 0; i--) {
                if (levels[i].width >= width) {
                    level = levels[i];
                }
            }
            if (
                level.cache.width === width &&
                level.cache.height === height &&
                level.cache.shouldNormalizeHeight === shouldNormalizeHeight
            ) {
                return level.cache.path;
            }
            const path = scaleWaveform0to1(
                level.normalizedPath,
                width,
                height,
                shouldNormalizeHeight,
            );
            level.cache = {
                width,
                height,
                shouldNormalizeHeight,
                path,
            };
            return path;
        },
    };
};
